import type { Address } from '~/types/location'
import * as Sentry from '@sentry/vue'
import { toRentalInstant } from '~/composables/prebooking-date'
import { useAppStore } from '~/stores/appstore'
import { useDateTimeStore } from '~/stores/datetime'
import { useLocationStore } from '~/stores/location'
import { trackClientSide } from '~/utils/tracking'

export interface AddressSearchTrackingData {
  address: string
  latitude: number
  longitude: number
  startDateTime: string
  endDateTime: string
  isBookable: boolean
  sessionId: string
  locationId: number
  customerUuid?: string | null
  userTypedAddress?: string | null
}

export function usePrebookingTracking() {
  const appStore = useAppStore()

  async function getSessionId(): Promise<string> {
    return await appStore.getSessionId()
  }

  const trackValidAddressSearch = async (): Promise<void> => {
    try {
      const locationStore = useLocationStore()
      const dateTimeStore = useDateTimeStore()

      if (!locationStore.address?.location) {
        if (import.meta.server) {
          console.warn('Tracking: no valid address location data')
        }
        return
      }

      const trackingData: AddressSearchTrackingData = {
        address: locationStore.address.info,
        latitude: locationStore.address.location.latitude,
        longitude: locationStore.address.location.longitude,
        startDateTime: dateTimeStore.startDate.date ? toRentalInstant(dateTimeStore.startDate.date).toISOString() : '',
        endDateTime: dateTimeStore.endDate.date ? toRentalInstant(dateTimeStore.endDate.date).toISOString() : '',
        isBookable: !locationStore.addressError,
        sessionId: await getSessionId(),
        locationId: locationStore.city.locationId,
        customerUuid: appStore.customerUuid || null,
        userTypedAddress: locationStore.lastTypedQuery || null,
      }

      await trackClientSide('api/rental/prebooking-service/external/prebooking/track/address-search', trackingData)
    }
    catch (error: unknown) {
      Sentry.captureException(error)
    }
  }

  const trackOutOfBusinessAreaAddressSearch = async (address: Address | undefined, userTypedAddress?: string): Promise<void> => {
    try {
      const locationStore = useLocationStore()
      const trackingData: Partial<AddressSearchTrackingData> = {
        address: address?.info,
        latitude: address?.location?.latitude,
        longitude: address?.location?.longitude,
        isBookable: !locationStore.addressError,
        sessionId: await getSessionId(),
        locationId: locationStore.city.locationId,
        customerUuid: appStore.customerUuid || null,
        userTypedAddress: userTypedAddress || null,
      }

      await trackClientSide('api/rental/prebooking-service/external/prebooking/track/out-of-business-area-search', trackingData)
    }
    catch (error: unknown) {
      Sentry.captureException(error)
    }
  }

  return {
    trackValidAddressSearch,
    trackOutOfBusinessAreaAddressSearch,
  }
}
